import type { Course, Session } from '@mc/api'
import {
  subcatsFixturePALM as subcatsFixture,
  coursesFixturePALM as coursesFixture,
} from '@mc/growth-data'
import { get } from 'libs/client/api-service'
import { createClientUtils } from '../clientUtils'

const {
  getCategoryIds,
  getPrimaryCategoryId,
  courseCategoryMatches,
  replaceCourseCategories,
  getCategoriesFromFixture,
} = createClientUtils({
  subcatsFixture,
  coursesFixture,
})

export const getCamps = async (url: string) => {
  const ret = await get(url) // either deep=true or not
  return (ret as Session[]).map((camp) => ({
    ...camp,
    categories: getCategoryIds(camp.slug).map((id) => ({ id })),
    primaryCategory: { id: getPrimaryCategoryId(camp.slug) },
  }))
}

// Since the courses by category fetcher makes the same large API call for all courses,
// we want to guarantee its only called once as needed, then cached for all subsequent calls.
//
// Note: we only implement this for the PALM taxonomy client because its the winner
// and therefore the only client that is live in production.
// Same pattern should apply to the other clients but it would be dead code.
//
// To achieve non-concurrency we use a simple semaphore with one slot.
// Thanks to https://blog.battlefy.com/limiting-concurrency-with-promises for semaphore implementation.
//
let semaphore: Promise<void> | undefined
let unlock: (() => void) | undefined

const acquire = async () => {
  await semaphore
  semaphore = new Promise((resolve) => {
    unlock = resolve
  })
}

const release = () => {
  unlock?.()
  semaphore = undefined
  unlock = undefined
}

let cache: Course[] | null = null

const nonConcurrentGetCourses = async () => {
  try {
    await acquire()
    if (!cache) {
      const url = '/jsonapi/v1/courses' // not ?deep=true
      const result = await get(url)
      cache = result as Course[]
    }
    return cache
  } finally {
    release()
  }
}

export const getCoursesByCategorySlug = async (url: string) => {
  const resp = await nonConcurrentGetCourses()
  return resp
    .filter((course) => courseCategoryMatches(course, url))
    .map((course) => replaceCourseCategories(course, url))
}

export const getCategories = async () => getCategoriesFromFixture()
